import React, { useState } from 'react';
import { X, Clock, Flame, Calendar, Share2, Trash2, ThumbsUp } from 'lucide-react';
import { BreadAnalysis } from '../../types/analysis';
import { deleteAnalysis } from '../../lib/analysisStore';
import { AnalysisChart } from './AnalysisChart';
import { FeedbackSection } from './FeedbackSection';

interface AnalysisDetailsModalProps {
  analysis: BreadAnalysis;
  onClose: () => void;
  onDelete?: () => void;
  onKnowledgeClick?: () => void;
}

export function AnalysisDetailsModal({
  analysis,
  onClose,
  onDelete,
  onKnowledgeClick
}: AnalysisDetailsModalProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError(null);
      await deleteAnalysis(analysis.id);
      if (onDelete) {
        onDelete();
      }
      onClose();
    } catch (err) {
      console.error('Error deleting analysis:', err);
      setError('Failed to delete analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleShareResults = () => {
    window.open('https://www.facebook.com/groups/1875669956357222/', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl text-white font-brand font-light italic">Analysis Results</h2>
            <button 
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-500/10 backdrop-blur-sm text-red-200 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Image */}
          {analysis.imageUrl && (
            <img
              src={analysis.imageUrl}
              alt="Analyzed bread"
              className="w-full rounded-xl"
            />
          )}

          {/* Comments */}
          <div className="p-4 bg-white/5 rounded-xl">
            <h3 className="font-medium mb-2 text-white">Baker's Comments</h3>
            <p className="text-white/80 whitespace-pre-line">{analysis.comments}</p>
          </div>

          {/* Analysis Chart */}
          <div className="flex justify-center">
            <AnalysisChart ratings={analysis.ratings} />
          </div>

          {/* Feedback */}
          <FeedbackSection
            ratings={analysis.ratings}
            parameters={analysis.parameters}
            onKnowledgeClick={onKnowledgeClick}
          />
        </div>

        <div className="p-6 border-t border-white/10 bg-white/5">
          <div className="flex justify-between items-center">
            {!showDeleteConfirm && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-6 py-2 text-red-300 hover:text-red-200 hover:bg-red-500/10 rounded-xl transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}

            {showDeleteConfirm ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-6 py-2 border border-white/20 rounded-xl hover:bg-white/10 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 bg-red-500/90 hover:bg-red-500 text-white rounded-xl transition-all duration-300"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Confirm Delete
                    </>
                  )}
                </button>
              </div>
            ) : (
              <button
                onClick={onClose}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-500/90 to-blue-600/90 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl transition-all duration-300"
              >
                <ThumbsUp className="w-4 h-4" />
                Got it
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}